/* Pet Cameo | v34 | Production date: 2026-10-02 | Matt Font-AI-ne */
(() => {
  'use strict';
  const form = document.querySelector('#create-form');
  if (!form) return;
  const $ = selector => document.querySelector(selector);
  const pcAsset = name => (window.PetCameoAssets && window.PetCameoAssets[name]) || '';
  const imageLoaded = image => new Promise((resolve, reject) => { if (image.complete && image.naturalWidth) return resolve(); image.onload = () => resolve(); image.onerror = () => reject(new Error('Image failed to load.')); });
  // ---- state -----------------------------------------------------------------------------
  // Everything the form offers comes from the API's store specs ("styles"): the categories, the scenes, and
  // the fields asked for with their labels, defaults and show_when rules. Only the designer's category names,
  // category icons and scene thumbnails are looked up locally, and are optional.
  let styles = [];
  let style = null;          // the selected style
  let fields = [];           // rendered field configs of the selected style, in spec order
  let sceneValue = '';       // selected scene label
  let uploadSequence = 0;
  let photoFile = null;
  let photoUrl = null;
  let currentArtwork = null;
  let busy = false, hasGeneratedPreview = false;
  let openPicker = null;
  const pickers = [];

  // Designer's names and icons for the styles they drew; any other style falls back to its API name.
  const knownCategories = {
    'outdoor-pet.spec.json': { id: 'outdoors', label: 'Outdoors', title: 'Outdoor' },
    'pet-at-work.spec.json': { id: 'work', label: 'Work', title: 'Work' },
    'pet-in-the-band.spec.json': { id: 'band', label: 'Band', title: 'Band' },
    'sports-pet.spec.json': { id: 'sports', label: 'Sports', title: 'Sports' }
  };
  // Designer's scene thumbnails by the API's scene label. Optional: a scene without one gets a conventional file name.
  const knownThumbs = {
    'Hunting': 'Outdoor-Hunting', 'Fishing': 'Outdoor-Fishing', 'Camping': 'Outdoor-Camping',
    'Mountaineering': 'Outdoor-Mountaineering', 'Rock climbing': 'Outdoor-RockClimbing',
    'Construction worker': 'Work-Contruction', 'Doctor': 'Work-Doctor', 'Astronaut': 'Work-Astronaut', 'Farmer': 'Work-Farmer',
    'Soloist / Crooner': 'Band-Crooner', 'Rock band: guitar': 'Band-RockGuitar', 'Rock band: drums': 'Band-RockDrums',
    'Rock band: bass': 'Band-RockBass', 'Rock band: singer': 'Band-RockSinger', 'Jazz trio': 'Band-JazzTrio', 'K-pop': 'Band-Kpop',
    'Golf': 'Sports-Golf', 'Tennis': 'Sports-Tennis', 'Football': 'Sports-Football', 'Soccer': 'Sports-Soccer', 'Baseball': 'Sports-Baseball'
  };
  const slug = value => String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const stripColon = text => String(text || '').replace(/\s*:\s*$/, '');

  // ---- reading the spec ------------------------------------------------------------------
  // Same field types and shapes the storefront block understood (text-field, radio-button, select, check-box,
  // color-chips, image-upload); an unknown type is skipped rather than breaking the form.
  const optionsOf = raw => Array.isArray(raw) ? raw : [];
  function parseStyle(spec) {
    const category = knownCategories[spec.filename] || { id: slug(spec.name || spec.filename), label: spec.name || spec.filename, title: spec.name || spec.filename };
    const parsed = { spec, filename: spec.filename, name: spec.name, category, hasImage: false, fields: [] };
    (spec['user-options'] || []).forEach((definition, index) => {
      const kind = Object.keys(definition || {})[0];
      const raw = definition[kind];
      const showRaw = raw && typeof raw === 'object' && raw.show_when && typeof raw.show_when === 'object' ? raw.show_when : null;
      const when = showRaw && Object.entries(showRaw).find(([, values]) => Array.isArray(values));
      const showWhen = when ? { field: when[0], values: when[1].map(String) } : null;
      const name = raw && typeof raw === 'object' && typeof raw.name === 'string' ? raw.name.trim() : '';
      if (kind === 'image-upload') { parsed.hasImage = true; return; }
      if (kind === 'text-field') {
        const config = typeof raw === 'string' ? { label: raw } : raw || {};
        parsed.fields.push({ type: 'text', key: name || 'text_' + index, label: stripColon(config.label || 'Text'), placeholder: config.placeholder || '', rows: Number.isInteger(config.size) && config.size > 1 ? config.size : 1, showWhen });
      } else if (kind === 'radio-button') {
        const group = Object.entries(raw || {}).find(([key, value]) => key !== 'name' && Array.isArray(value));
        if (!group || !group[1].length) return;
        parsed.fields.push({ type: name === 'scene' ? 'scene' : 'radio', key: name || 'radio_' + index, label: stripColon(group[0]), options: group[1].map(String), showWhen });
      } else if (kind === 'select') {
        const list = optionsOf(raw && raw.options).map(String);
        if (!list.length) return;
        parsed.fields.push({ type: 'select', key: name || 'select_' + index, label: stripColon(raw.label || 'Select'), options: list, value: typeof raw.default === 'string' ? raw.default : list[0], showWhen });
      } else if (kind === 'check-box') {
        const label = typeof raw === 'string' ? raw : Object.keys(raw || {}).find(key => !['name', 'default', 'show_when'].includes(key)) || 'Enable option';
        parsed.fields.push({ type: 'check', key: name || 'check_' + index, label, checked: !!(raw && raw.default), showWhen });
      } else if (kind === 'color-chips') {
        const chips = optionsOf(raw && raw.options).filter(option => option && option.label);
        if (!chips.length) return;
        parsed.fields.push({ type: 'chips', key: name || 'chips_' + index, label: stripColon(raw.label || 'Color'), options: chips, value: typeof raw.default === 'string' ? raw.default : chips[0].label, showWhen });
      }
    });
    return parsed;
  }
  const sceneField = candidate => candidate && candidate.fields.find(field => field.type === 'scene');

  // Custom listboxes: icons and keyboard support.
  // Arrows, Home/End, typing, Enter, Escape, Tab.
  function picker(container, labelId, options, value, onChange) {
    container.replaceChildren();
    const trigger = document.createElement('button');
    trigger.type = 'button'; trigger.className = 'picker-trigger';
    trigger.id = container.id + '-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-labelledby', labelId + ' ' + trigger.id);
    const list = document.createElement('ul');
    list.className = 'picker-list'; list.id = container.id + '-list'; list.hidden = true;
    list.setAttribute('role', 'listbox'); list.setAttribute('aria-labelledby', labelId);
    trigger.setAttribute('aria-controls', list.id);
    let active = options.findIndex(item => item.id === value);
    function content(element, option) {
      if (option.icon) {
        const image = document.createElement('img'); image.src = option.icon; image.alt = ''; element.append(image);
      }
      const label = document.createElement('span'); label.textContent = option.label; element.append(label);
    }
    content(trigger, options[active]);
    const nodes = options.map((option, index) => {
      const node = document.createElement('li'); node.className = 'picker-option'; node.tabIndex = -1;
      node.setAttribute('role', 'option'); node.setAttribute('aria-selected', String(option.id === value));
      content(node, option); list.append(node);
      node.addEventListener('click', () => { close(false); onChange(option.id); document.getElementById(trigger.id)?.focus(); });
      node.addEventListener('focus', () => { active = index; });
      return node;
    });
    function close(restore = true) { list.hidden = true; trigger.setAttribute('aria-expanded', 'false'); if (openPicker === control) openPicker = null; if (restore) trigger.focus(); }
    function open() {
      if (openPicker) openPicker.close(false);
      list.hidden = false; trigger.setAttribute('aria-expanded', 'true'); openPicker = control;
      nodes[active].focus();
    }
    const control = { close, container };
    trigger.addEventListener('click', () => list.hidden ? open() : close());
    trigger.addEventListener('keydown', event => {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); open(); }
    });
    list.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key === 'Tab') { close(false); trigger.focus(); return; }
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); nodes[active].click(); return; }
      let next = active;
      if (event.key === 'ArrowDown') next = (active + 1) % nodes.length;
      else if (event.key === 'ArrowUp') next = (active - 1 + nodes.length) % nodes.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = nodes.length - 1;
      else if (event.key.length === 1) {
        const match = options.findIndex((option, index) => index > active && option.label.toLowerCase().startsWith(event.key.toLowerCase()));
        next = match >= 0 ? match : options.findIndex(option => option.label.toLowerCase().startsWith(event.key.toLowerCase()));
        if (next < 0) return;
      } else return;
      event.preventDefault(); nodes[next].focus();
    });
    container.append(trigger, list); pickers.push(control);
  }
  const category = () => style && style.category;
  function currentValue(field) {
    if (field.type === 'scene') return sceneValue;
    if (field.type === 'text') return field.node.querySelector('input, textarea').value.trim();
    if (field.type === 'select') return field.node.querySelector('select').value;
    if (field.type === 'check') return field.node.querySelector('input').checked;
    const checked = field.node.querySelector('input:checked');
    return checked ? checked.value : '';
  }
  // A field with show_when is shown only while the field it names holds one of the listed values.
  function applyVisibility() {
    fields.forEach(field => {
      if (!field.showWhen) return;
      const controller = fields.find(item => item.key === field.showWhen.field);
      field.node.hidden = !(controller && field.showWhen.values.includes(String(currentValue(controller))));
    });
  }
  function renderPickers() {
    if (openPicker) openPicker.close(false);
    pickers.length = 0;
    if (!styles.length) return;
    picker($('#category-picker'), 'category-label', styles.map(item => ({ id: item.filename, label: item.category.label, icon: pcAsset('icon-category-' + item.category.id + '.png') })), style.filename, id => {
      selectStyle(styles.find(item => item.filename === id));
    });
    const scene = sceneField(style);
    if (scene && $('#scene-picker')) {
      picker($('#scene-picker'), 'scene-label', scene.options.map(label => ({ id: label, label })), sceneValue, value => {
        sceneValue = value; renderPickers(); applyVisibility(); invalidateArtwork();
      });
    }
  }
  function field(label, id) {
    const wrapper = document.createElement('div'); wrapper.className = 'field';
    const text = document.createElement('label'); text.className = 'field-label'; text.textContent = label; if (id) text.htmlFor = id;
    wrapper.append(text); return { wrapper, text };
  }
  function swatches(container, name, chips, selected) {
    chips.forEach(chip => {
      const wrapper = document.createElement('label'); wrapper.className = 'swatch'; wrapper.style.setProperty('--swatch', chip.color); wrapper.title = chip.label;
      const input = document.createElement('input'); input.type = 'radio'; input.name = name; input.value = chip.label;
      input.checked = chip.label === selected; input.setAttribute('aria-label', chip.label);
      wrapper.append(input, document.createElement('span')); container.append(wrapper);
    });
  }
  // Builds one control per spec field, in the order the spec declares them.
  function renderFields() {
    const container = $('#dynamic-fields');
    container.replaceChildren();
    fields = style.fields.map((definition, index) => {
      const config = { ...definition };
      const id = 'field-' + index + '-' + slug(config.key);
      let node;
      if (config.type === 'scene') {
        node = document.createElement('div'); node.className = 'field';
        const row = document.createElement('div'); row.className = 'field-label-row';
        const label = document.createElement('span'); label.className = 'field-label'; label.id = 'scene-label'; label.textContent = config.label;
        const view = document.createElement('button'); view.type = 'button'; view.className = 'text-link'; view.id = 'view-scenes'; view.textContent = 'View Scenes';
        view.addEventListener('click', () => openDialog($('#scenes-dialog')));
        row.append(label, view);
        const holder = document.createElement('div'); holder.id = 'scene-picker'; holder.className = 'custom-picker';
        node.append(row, holder);
      } else if (config.type === 'text') {
        const { wrapper } = field(config.label, id); node = wrapper;
        const input = document.createElement(config.rows > 1 ? 'textarea' : 'input');
        if (config.rows > 1) input.rows = config.rows; else input.type = 'text';
        input.id = id; input.name = config.key; input.placeholder = config.placeholder; input.autocomplete = 'off'; input.maxLength = 300;
        node.append(input);
      } else if (config.type === 'select') {
        const { wrapper } = field(config.label, id); node = wrapper;
        const select = document.createElement('select'); select.id = id; select.name = config.key;
        config.options.forEach(option => { const item = new Option(option, option); item.selected = option === config.value; select.add(item); });
        node.append(select);
      } else if (config.type === 'check') {
        node = document.createElement('div'); node.className = 'all-star';
        const label = document.createElement('label'); label.htmlFor = id; label.textContent = config.label;
        const input = document.createElement('input'); input.type = 'checkbox'; input.id = id; input.name = config.key; input.checked = config.checked;
        node.append(label, input);
      } else if (config.type === 'chips') {
        node = document.createElement('fieldset'); node.className = 'color-field';
        const legend = document.createElement('legend'); legend.className = 'field-label'; legend.textContent = config.label;
        const holder = document.createElement('div'); holder.className = 'swatches';
        swatches(holder, id, config.options, config.value);
        node.append(legend, holder);
      } else {
        node = document.createElement('fieldset'); node.className = 'color-field';
        const legend = document.createElement('legend'); legend.className = 'field-label'; legend.textContent = config.label;
        node.append(legend);
        config.options.forEach((option, optionIndex) => {
          const row = document.createElement('label'); row.className = 'choice';
          const input = document.createElement('input'); input.type = 'radio'; input.name = id; input.value = option; input.checked = optionIndex === 0;
          row.append(input, document.createTextNode(' ' + option)); node.append(row);
        });
      }
      node.dataset.fieldKey = config.key;
      config.node = node;
      container.append(node);
      return config;
    });
    $('#photo-section').hidden = !style.hasImage;
    applyVisibility();
  }
  function selectStyle(next, scene) {
    style = next;
    const scenes = sceneField(style);
    sceneValue = scenes ? (scene && scenes.options.includes(scene) ? scene : scenes.options[0]) : '';
    renderFields(); renderPickers(); invalidateArtwork();
  }
  function invalidateArtwork() {
    // Changed choices? Regenerate. No stale-design bullshit.
    if (!currentArtwork) return;
    currentArtwork = null; $('#shop-art').setAttribute('aria-disabled', 'true'); $('#shop-art').tabIndex = -1;
    message($('#generation-status'), 'Your choices changed. Generate again to update your artwork.');
  }
  document.addEventListener('click', event => { if (openPicker && !openPicker.container.contains(event.target)) openPicker.close(false); });
  form.addEventListener('input', () => { applyVisibility(); invalidateArtwork(); });

  // Styles from the API; whatever it serves as available is what the form offers.
  const generation = $('#generation-status');
  if (typeof window.PetCameoAPI?.getStyles !== 'function') {
    message(generation, 'The create form is not connected yet.', true);
  } else {
    $('#generate-button').disabled = true;
    window.PetCameoAPI.getStyles().then(specs => {
      styles = specs.map(parseStyle).filter(item => sceneField(item) || item.fields.length);
      if (!styles.length) throw new Error('No styles are available right now. Please check back soon.');
      $('#generate-button').disabled = false;
      selectStyle(styles[0]); buildSceneGrid();
    }).catch(error => message(generation, error && error.message ? error.message : 'Could not load the styles. Please refresh the page.', true));
  }

  function message(element, text, error = false) { element.textContent = text; element.classList.toggle('error', error); }
  $('#upload-button').addEventListener('click', () => $('#photo-input').click());
  $('#photo-input').addEventListener('change', async event => {
    if (busy) return;
    const sequence = ++uploadSequence;
    const file = event.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { message($('#upload-status'), 'Please choose a JPG or PNG photo.', true); event.target.value = ''; return; }
    if (file.size > 20 * 1024 * 1024) { message($('#upload-status'), 'Please choose a photo smaller than 20 MB.', true); event.target.value = ''; return; }
    const url = URL.createObjectURL(file);
    try {
      const image = new Image(); image.src = url; await imageLoaded(image);
      if (sequence !== uploadSequence) { URL.revokeObjectURL(url); return; }
      if (image.naturalWidth * image.naturalHeight > 40000000) throw new Error('Photo dimensions exceed the preview limit.');
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      photoUrl = url; photoFile = file; $('#pet-photo').src = url; $('#pet-photo').classList.remove('default-pet-photo'); $('#pet-photo').alt = 'Your uploaded pet photo';
      message($('#upload-status'), 'Photo uploaded.'); invalidateArtwork();
    } catch {
      URL.revokeObjectURL(url); if (sequence !== uploadSequence) return; message($('#upload-status'), 'That photo could not be opened. Please try another JPG or PNG.', true);
    }
    event.target.value = '';
  });

  // Native dialogs handle focus and Escape.
  function openDialog(dialog) { if (openPicker) openPicker.close(false); dialog.showModal(); document.body.classList.add('modal-open'); }
  [$('#scenes-dialog'), $('#art-dialog')].forEach(dialog => {
    dialog.querySelector('.dialog-close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => document.body.classList.remove('modal-open'));
  });
  // Thumbnails: the designer's file when we know it; otherwise a conventional file name
  // (pc-tmb-<category>-<scene>.png, with or without hyphens in the scene part) if one has been uploaded to the theme.
  function setThumbnail(image, categoryItem, label) {
    const asset = knownThumbs[label];
    const known = asset && pcAsset('Scene-Thumbnails/Tmb-' + asset + '.png');
    if (known) { image.src = known; return; }
    const reference = pcAsset('Scene-Thumbnails/Tmb-Outdoor-Camping.png');
    const base = reference.slice(0, reference.lastIndexOf('/') + 1);
    const names = [...new Set([slug(label), slug(label).replace(/-/g, '')])].map(name => 'pc-tmb-' + slug(categoryItem.title) + '-' + name + '.png');
    if (!base) { image.hidden = true; return; }
    let index = 0;
    image.addEventListener('error', () => { index += 1; if (index < names.length) image.src = base + names[index]; else image.hidden = true; });
    image.src = base + names[0];
  }
  function buildSceneGrid() {
    $('#scene-grid').replaceChildren();
    styles.forEach(item => {
      const scenes = sceneField(item);
      if (!scenes) return;
      scenes.options.forEach(label => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'scene-card';
        const title = document.createElement('span'); title.textContent = item.category.title + ': ' + label;
        const image = document.createElement('img'); image.alt = label + ' example artwork'; image.loading = 'lazy';
        setThumbnail(image, item.category, label);
        button.append(title, image); $('#scene-grid').append(button);
        button.addEventListener('click', () => {
          if (busy) return;
          selectStyle(item, label); $('#scenes-dialog').close();
          const trigger = $('#scene-picker-trigger'); if (trigger) trigger.focus();
        });
      });
    });
  }
  $('#art-preview').addEventListener('click', () => { $('#enlarged-art').src = $('#generated-art').src; openDialog($('#art-dialog')); });

  function getRequest() {
    const options = {};
    fields.forEach(config => {
      if (config.node.hidden) return;
      const value = currentValue(config);
      // A blank text field is left out: its placeholder is a hint, and the artist applies the style's own default.
      if (config.type === 'text' && !value) return;
      options[config.key] = value;
    });
    return {
      photo: photoFile, storeSpec: style.filename, category: style.category.id, scene: sceneValue,
      petName: typeof options.name === 'string' ? options.name : '', options
    };
  }
  function setBusy(value) {
    busy = value;
    if (value && openPicker) openPicker.close(false);
    form.querySelectorAll('input, select, textarea, button, fieldset').forEach(node => { node.disabled = value; });
    form.setAttribute('aria-busy', String(value));
    $('#generate-button').classList.toggle('is-generating', value);
    $('#art-preview').classList.toggle('is-generating', value);
    $('#art-preview').disabled = value || !hasGeneratedPreview;
    $('#generate-button span').textContent = value ? 'Generating…' : 'Generate Art';
  }
  form.addEventListener('submit', async event => {
    event.preventDefault(); if (busy || !style) return;
    if (style.hasImage && !photoFile) { message($('#upload-status'), 'Upload your pet’s photo first.', true); $('#upload-button').focus(); return; }
    const request = getRequest();
    // George owns generation. Keep secret shit server-side.
    const generate = window.PetCameoAPI?.generateArtwork;
    if (typeof generate !== 'function') { message($('#generation-status'), 'Image generation is not connected yet. Your photo and choices are ready.', true); return; }
    if ($('#scenes-dialog').open) $('#scenes-dialog').close();
    ++uploadSequence;
    setBusy(true); message($('#generation-status'), 'Creating your pet’s artwork…');
    try {
      const result = await generate.call(window.PetCameoAPI, request);
      if (!result || typeof result.imageUrl !== 'string') throw new Error('The generation service did not return an image.');
      const url = new URL(result.imageUrl, location.href);
      if (!['https:', 'http:', 'blob:'].includes(url.protocol) && !(url.protocol === 'data:' && /^data:image\/(png|jpeg|webp);base64,/i.test(result.imageUrl))) throw new Error('The generation service returned an unsupported image URL.');
      const image = new Image(); image.src = result.imageUrl; await imageLoaded(image);
      const art = $('#generated-art'); art.src = result.imageUrl; art.alt = (request.petName ? request.petName + ' — ' : '') + (request.scene || style.name) + ' artwork'; art.classList.remove('placeholder-art');
      currentArtwork = { ...result, request };
      // Save now; Cart needs the artwork context.
      try { await saveArtwork(currentArtwork); } catch { /* Storage failed; keep the generated art usable. */ }
      hasGeneratedPreview = true; $('#art-preview').disabled = false;
      $('#shop-art').setAttribute('aria-disabled', 'false'); $('#shop-art').tabIndex = 0;
      message($('#generation-status'), 'Your artwork is ready!');
      window.dispatchEvent(new CustomEvent('petcameo:artwork-ready', { detail: currentArtwork }));
    } catch (error) {
      message($('#generation-status'), error instanceof Error ? error.message : 'Something went wrong. Please try again.', true);
    } finally { setBusy(false); }
  });
  async function saveArtwork(artwork) {
        let imageUrl = artwork.imageUrl;
        if (imageUrl.startsWith('blob:')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          imageUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(blob);
          });
        }
        const { photo, ...choices } = artwork.request;
        sessionStorage.setItem('petcameo:artwork', JSON.stringify({ imageUrl, artworkId: artwork.artworkId, jobId: artwork.jobId, request: choices }));
  }
  $('#shop-art').addEventListener('click', event => {
    event.preventDefault(); if (busy || !currentArtwork) return;
    // Adapter owns persistence and navigation.
    if (typeof window.PetCameoAPI?.openShop === 'function') {
      Promise.resolve().then(() => window.PetCameoAPI.openShop(currentArtwork)).catch(() => message($('#generation-status'), 'Could not open the shop. Please try again.', true));
    } else {
      // Local preview fallback.
      Promise.resolve().then(async () => {
        await saveArtwork(currentArtwork);
        location.href = (window.PetCameoConfig && window.PetCameoConfig.routes && window.PetCameoConfig.routes.shop) || 'shop.html';
      }).catch(() => message($('#generation-status'), 'Could not save your artwork for Shop. Please try again.', true));
    }
  });
  window.PetCameoCreate = { getRequest, getArtwork: () => currentArtwork, getStyles: () => styles };
  window.addEventListener('pagehide', () => { if (photoUrl) URL.revokeObjectURL(photoUrl); });
})();
